import { notifications } from '@mantine/notifications';
import { ApiError } from 'src/error/ApiError';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { Logger } from 'src/utils/Logger';

export const UPLOAD_CONCURRENCY = 5;

function isRetryable(error: unknown): boolean {
  return !(error instanceof ApiError && error.status < 500);
}

async function withRetry<T>(task: () => Promise<T>): Promise<T> {
  try {
    return await task();
  } catch (error) {
    if (isRetryable(error)) {
      return await task();
    }
    throw error;
  }
}

type UploadPoolOptions = {
  concurrency: number;
  context?: Record<string, unknown>;
  onSettled?: () => void;
};

export async function uploadPool<T>(
  items: { task: () => Promise<T>; label: string }[],
  options: UploadPoolOptions,
): Promise<T[]> {
  const { concurrency, context = {}, onSettled } = options;
  const results: PromiseSettledResult<T>[] = new Array(items.length);
  let next = 0;

  const worker = async () => {
    while (next < items.length) {
      const i = next++;
      try {
        results[i] = { status: 'fulfilled', value: await withRetry(items[i].task) };
      } catch (reason) {
        results[i] = { status: 'rejected', reason };
      }
      onSettled?.();
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, worker));

  const successes: T[] = [];
  for (let i = 0; i < results.length; i++) {
    const result = results[i];
    if (result.status === 'fulfilled') {
      successes.push(result.value);
    } else if (ApiError.isGloballyHandledError(result.reason)) {
      // The pool settles every file, so the mutation resolves and App.tsx's global handler never sees
      // this error; notifyAndLogError would stay quiet on the assumption that it had.
      notifications.show({
        title: `Failed to upload ${items[i].label}`,
        message: result.reason.message,
        color: 'red',
      });
      Logger.warn(`Failed to upload ${items[i].label}: ${result.reason.message}`, context);
    } else {
      notifyAndLogError(
        `Failed to upload ${items[i].label}`,
        'An error occurred while uploading this file.',
        result.reason,
        context,
      );
    }
  }

  return successes;
}
